import React, {useMemo} from 'react';
import {
    createOrderThunk,
    getOrderThunk,
    listClientOrders,
    listOrdersThunk,
    listWorkerOrders,
    updateOrderThunk
} from "./orders.thunk";
import {selectAllOrders, selectOrderById, selectOrderListLoadingState} from "./orders.slice";
import {useAppDispatch, useAppSelector} from "../../../redux/redux.hook";
import {CreateOrderData, FeOrder, UpdateOrderInput} from "./types";
import {batch} from "react-redux";
import {createSelector} from "@reduxjs/toolkit";
import {FeAvailability} from "../../availibilities/redux/types";
import {makeSelectWorkerDays} from "../../workers/redux/workers.slice";
import {listWorkerAvailabilities} from "../../availibilities/redux/availabilities.thunk";
import {endOfMonth, getDaysInMonth, startOfMonth} from "date-fns";

export function useClientOrders(clientId: string) {

    const [loading, setLoading] = React.useState(false);

    const dispatch = useAppDispatch();

    const orders = useAppSelector(
        createSelector(
            selectAllOrders,
            (orders) => orders.filter(o => o.clientId === clientId)
        ));

    React.useEffect(() => {
        setLoading(true);
        dispatch(listClientOrders({clientId}))
            .catch(() => {
            })
            .finally(() => setLoading(false));
    }, [clientId, dispatch]);

    return {orders, loading};
}

export type WorkerEvent =
    { type: 'order' } & FeOrder
    | { type: 'reservation' } & FeOrder
    | { type: 'availability' } & FeAvailability

export function useWorkerCalendarDays(workerId: string, selectedMonth: Date) {

    const dispatch = useAppDispatch();

    const firstOfMonth = useMemo(() => startOfMonth(selectedMonth).getTime(), [selectedMonth]);
    const lastOfMonth = useMemo(() => endOfMonth(selectedMonth).getTime(), [selectedMonth]);
    const daysInMonth = useMemo(() => getDaysInMonth(selectedMonth), [selectedMonth])

    const {
        workerDays,
        workerOrders,
        workerAvailabilities
    } = useAppSelector(state => makeSelectWorkerDays()(state, {workerId, selectedMonth, daysInMonth}));

    React.useEffect(() => {
        batch(() => {
            if (workerId) {
                const timeRange = {
                    start: firstOfMonth,
                    end: lastOfMonth
                };
                dispatch(listWorkerOrders({workerId, timeRange}));
                dispatch(listWorkerAvailabilities({workerId, timeRange}))
            }
        });
    }, [dispatch, firstOfMonth, lastOfMonth, workerId]);

    return {workerDays, workerOrders, workerAvailabilities};
}

export function useCreateOrder() {
    const dispatch = useAppDispatch();
    return {createOrder: (dto: CreateOrderData) => dispatch(createOrderThunk(dto))};
}

export function useUpdateOrder() {
    const dispatch = useAppDispatch();
    return {updateOrder: (id: string, dto: UpdateOrderInput) => dispatch(updateOrderThunk({id, dto}))};
}

const selectAll = createSelector(
    selectAllOrders,
    selectOrderListLoadingState,
    (orders, loadingState) => ({
        orders,
        loadingState
    }));

export function useAllOrders() {
    const dispatch = useAppDispatch();
    const all = useAppSelector(selectAll);
    React.useEffect(() => {
        dispatch(listOrdersThunk())
    }, [dispatch]);
    return all;
}

export function useOrder(id: string) {
    const dispatch = useAppDispatch();

    const order = useAppSelector((state) => selectOrderById(state, id));
    /*const error = useAppSelector((state) => state.orders.errors[id]);*/

    React.useEffect(() => {
        if (!order) {
            dispatch(getOrderThunk({id}))
        }
    }, [dispatch, id, order])

    return {
        order
    }
}
